import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/apiClient";
import { REQUEST_TYPES } from "@/lib/crmConstants";
import { RequestTypeBadge, StageBadge } from "@/components/Badges";
import { ArrowUpRight, Inbox, TrendingUp, Timer, Globe, HandMetal } from "lucide-react";

function KpiCard({ label, value, sublabel, testId, icon }) {
  return (
    <div className="card-shell p-5" data-testid={testId}>
      <div className="flex items-start justify-between">
        <div className="data-label">{label}</div>
        {icon}
      </div>
      <div className="mt-3 font-heading font-black text-4xl tracking-tight">{value}</div>
      {sublabel && <div className="mt-1 text-xs text-[#71717A]">{sublabel}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const r = await api.get("/dashboard/summary");
      setData(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (err) return <div className="p-8 text-red-700">{String(err)}</div>;
  if (!data) return <div className="p-8 text-[#71717A]">Loading…</div>;

  const conv = data.conversion_rate_pct;

  return (
    <div className="p-8 space-y-8">
      <div className="flex items-end justify-between">
        <div>
          <div className="data-label mb-1">Operations dashboard</div>
          <h1 className="font-heading font-black text-4xl tracking-tight">Team overview</h1>
        </div>
        <div className="text-xs text-[#71717A]">Live from your workspace</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpi-grid">
        <KpiCard
          label="Total records"
          value={data.total}
          sublabel="Across your accessible teams"
          testId="kpi-total"
          icon={<Inbox className="w-4 h-4 text-[#71717A]" />}
        />
        <KpiCard
          label="Website submissions"
          value={data.by_source.website}
          sublabel={`vs ${data.by_source.manual} manual`}
          testId="kpi-website"
          icon={<Globe className="w-4 h-4 text-[#71717A]" />}
        />
        <KpiCard
          label="Sales conversion"
          value={conv == null ? "—" : `${conv}%`}
          sublabel="Won / (Won + Lost + open)"
          testId="kpi-conversion"
          icon={<TrendingUp className="w-4 h-4 text-[#71717A]" />}
        />
        <KpiCard
          label="Avg service resolution"
          value={
            data.avg_resolution_days?.contact_us_complaint != null
              ? `${data.avg_resolution_days.contact_us_complaint}d`
              : "—"
          }
          sublabel="From complaint to close"
          testId="kpi-resolution"
          icon={<Timer className="w-4 h-4 text-[#71717A]" />}
        />
      </div>

      {/* Breakdown by request type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-shell p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="data-label">Breakdown</div>
              <h3 className="font-heading font-bold text-lg mt-0.5">Volume by request type</h3>
            </div>
          </div>
          <div className="space-y-3">
            {Object.entries(data.by_type).map(([type, count]) => {
              const cfg = REQUEST_TYPES[type];
              const max = Math.max(...Object.values(data.by_type), 1);
              const pct = (count / max) * 100;
              return (
                <div key={type} data-testid={`by-type-${type}`}>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <div className="flex items-center gap-2">
                      <RequestTypeBadge requestType={type} />
                      <span className="text-[#71717A] text-xs">
                        Open: {data.open_counts?.[type] ?? 0}
                      </span>
                    </div>
                    <span className="font-mono font-medium">{count}</span>
                  </div>
                  <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
                    <div className="h-full bg-[#4F46E5]" style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="card-shell p-5">
          <div className="data-label">Source split</div>
          <h3 className="font-heading font-bold text-lg mt-0.5 mb-4">Website vs Manual</h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2"><Globe className="w-3.5 h-3.5 text-[#4F46E5]" /> Website</span>
                <span className="font-mono">{data.by_source.website}</span>
              </div>
              <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
                <div className="h-full bg-[#4F46E5]" style={{ width: `${(data.by_source.website / (data.total || 1)) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="flex items-center gap-2"><HandMetal className="w-3.5 h-3.5 text-[#059669]" /> Manual entry</span>
                <span className="font-mono">{data.by_source.manual}</span>
              </div>
              <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
                <div className="h-full bg-[#059669]" style={{ width: `${(data.by_source.manual / (data.total || 1)) * 100}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Territory workload */}
      {data.territory_workload && Object.keys(data.territory_workload).length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="territory-workload">
          {Object.entries(data.territory_workload).map(([team, buckets]) => (
            <div key={team} className="card-shell p-5">
              <div className="data-label">Territory workload</div>
              <h3 className="font-heading font-bold text-lg mt-0.5 capitalize mb-4">{team} team</h3>
              {buckets.length === 0 ? (
                <div className="text-sm text-[#71717A]">No records yet.</div>
              ) : (
                <div className="space-y-2">
                  {buckets.map((b) => {
                    const max = Math.max(...buckets.map((x) => x.count), 1);
                    const pct = (b.count / max) * 100;
                    return (
                      <div key={b.user_id || "unassigned"} data-testid={`workload-${team}-${b.user_id || "unassigned"}`}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className={b.user_id ? "font-medium" : "italic text-[#71717A]"}>{b.name}</span>
                          <span className="font-mono">{b.count}</span>
                        </div>
                        <div className="h-2 bg-[#F4F4F5] rounded-full overflow-hidden">
                          <div
                            className="h-full"
                            style={{ width: `${pct}%`, backgroundColor: b.user_id ? "#4F46E5" : "#A1A1AA" }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Recent */}
      <div className="card-shell overflow-hidden">
        <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5]">
          <div>
            <div className="data-label">Recent activity</div>
            <h3 className="font-heading font-bold text-lg mt-0.5">Latest 5 records</h3>
          </div>
          <Link
            to="/records"
            className="text-sm font-medium text-[#4F46E5] hover:underline flex items-center gap-1"
            data-testid="dashboard-view-all"
          >
            View all <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
        {data.recent?.length ? (
          <table className="data-table">
            <thead>
              <tr>
                <th>Ticket</th><th>Type</th><th>Stage</th><th>Source</th><th>Created</th><th></th>
              </tr>
            </thead>
            <tbody>
              {data.recent.map((r) => (
                <tr key={r.id} data-testid={`recent-row-${r.ticket_id}`}>
                  <td className="font-mono">{r.ticket_id}</td>
                  <td><RequestTypeBadge requestType={r.request_type} /></td>
                  <td><StageBadge stage={r.stage} requestType={r.request_type} /></td>
                  <td className="text-xs text-[#71717A]">{r.source}</td>
                  <td className="text-xs text-[#71717A]">{new Date(r.created_at).toLocaleString()}</td>
                  <td>
                    <Link to={`/records/${r.id}`} className="text-[#4F46E5] text-sm hover:underline">Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-8 text-center text-[#71717A] text-sm">
            No records yet. Submit a public form or create one manually.
          </div>
        )}
      </div>
    </div>
  );
}
